import { useRef } from 'react';
import { addDomElement } from 'utils/bom-dom-manipulation/add-dom-element';
import SpringConfigs from 'utils/constants/swarm/spring-configs';

type metaType = HTMLMetaElement | null;

export const useMetaData = () => {
  const ogUrlBase = useRef<string>();

  const getOldMetaData = () => {
    const ogDescriptionMeta: metaType = document.querySelector(
      'meta[property="og:description"]'
    );

    const descriptionMeta: metaType = document.querySelector(
      'meta[name="description"]'
    );

    const twitterDescriptionMeta: metaType = document.querySelector(
      'meta[name="twitter:description"]'
    );

    const itemPropDescriptionMeta: metaType = document.querySelector(
      'meta[itemprop="description"]'
    );

    return {
      ogDescriptionMeta: ogDescriptionMeta?.content || '',
      descriptionMeta: descriptionMeta?.content || '',
      twitterDescriptionMeta: twitterDescriptionMeta?.content || '',
      itemPropDescriptionMeta: itemPropDescriptionMeta?.content || ''
    };
  };

  const oldMetaData = getOldMetaData();

  const generateMetaData = (
    metaTitle: string,
    metaDescription: string,
    completeUrl: string,
    skipCanonicalAndOgUrl: boolean
  ) => {
    if (SpringConfigs.MOCKED_DATA) {
      return;
    }

    if (!ogUrlBase.current) {
      ogUrlBase.current = (
        document.querySelector('meta[property="og:url"]') as HTMLMetaElement
      )?.content;
    }

    setCanonical(completeUrl, skipCanonicalAndOgUrl);
    setOgUrlMeta(completeUrl, skipCanonicalAndOgUrl);
    setMetaTitle(metaTitle);
    setOgMetaTitle(metaTitle);
    setTwitterMetaTitle(metaTitle);
    setMetaDescription(metaDescription);
    setOgDescription(metaDescription);
    setTwitterDescription(metaDescription);
    setItemPropDescription(metaDescription);
  };

  const setMetaTitle = (metaTitle: string) => {
    if (document.title === metaTitle) {
      return;
    }

    document.title = metaTitle;
  };

  const setCanonical = (url: string, skipCanonicalAndOgUrl: boolean) => {
    const canonicalLink = document.querySelector('link[rel="canonical"]');

    if (canonicalLink) {
      if (!skipCanonicalAndOgUrl) {
        canonicalLink.setAttribute('href', url);
      }
    } else {
      addDomElement(
        [
          {
            elemAttr: 'rel',
            elemValue: 'canonical'
          },
          {
            elemAttr: 'href',
            elemValue: url
          }
        ],
        'link'
      );
    }
  };

  const setOgUrlMeta = (url: string, skipCanonicalAndOgUrl: boolean) => {
    const ogUrlMeta = document.querySelector('meta[property="og:url"]');

    if (ogUrlMeta) {
      if (!skipCanonicalAndOgUrl) {
        ogUrlMeta.setAttribute('content', url);
      }
    } else {
      addDomElement([
        {
          elemAttr: 'property',
          elemValue: 'og:url'
        },
        {
          elemAttr: 'content',
          elemValue: url
        }
      ]);
    }
  };

  const setOgMetaTitle = (metaTitle: string) => {
    const ogTitleMeta = document.querySelector('meta[property="og:title"]');

    if (ogTitleMeta) {
      ogTitleMeta.setAttribute('content', metaTitle);
    } else {
      addDomElement([
        {
          elemAttr: 'property',
          elemValue: 'og:title'
        },
        {
          elemAttr: 'content',
          elemValue: metaTitle
        }
      ]);
    }
  };

  const setTwitterMetaTitle = (metaTitle: string) => {
    const twitterTitleMeta = document.querySelector(
      'meta[name="twitter:title"]'
    );

    if (twitterTitleMeta) {
      twitterTitleMeta.setAttribute('content', metaTitle);
    } else {
      addDomElement([
        {
          elemAttr: 'name',
          elemValue: 'twitter:title'
        },
        {
          elemAttr: 'content',
          elemValue: metaTitle
        }
      ]);
    }
  };

  const setMetaDescription = (metaDescription: string) => {
    const descriptionMeta = document.querySelector('meta[name="description"]');

    if (descriptionMeta) {
      descriptionMeta.setAttribute('content', metaDescription);
    } else {
      addDomElement([
        {
          elemAttr: 'name',
          elemValue: 'description'
        },
        {
          elemAttr: 'content',
          elemValue: metaDescription
        }
      ]);
    }
  };

  const setOgDescription = (metaDescription: string) => {
    const ogDescriptionMeta = document.querySelector(
      'meta[property="og:description"]'
    );

    if (ogDescriptionMeta) {
      ogDescriptionMeta.setAttribute('content', metaDescription);
    } else {
      addDomElement([
        {
          elemAttr: 'property',
          elemValue: 'og:description'
        },
        {
          elemAttr: 'content',
          elemValue: metaDescription
        }
      ]);
    }
  };

  const setTwitterDescription = (metaDescription: string) => {
    const twitterDescriptionMeta = document.querySelector(
      'meta[name="twitter:description"]'
    );

    if (twitterDescriptionMeta) {
      twitterDescriptionMeta.setAttribute('content', metaDescription);
    } else {
      addDomElement([
        {
          elemAttr: 'name',
          elemValue: 'twitter:description'
        },
        {
          elemAttr: 'content',
          elemValue: metaDescription
        }
      ]);
    }
  };

  const setItemPropDescription = (metaDescription: string) => {
    const itemPropDescriptionMeta = document.querySelector(
      'meta[itemprop="description"]'
    );

    if (itemPropDescriptionMeta) {
      itemPropDescriptionMeta.setAttribute('content', metaDescription);
    } else {
      addDomElement([
        {
          elemAttr: 'itemprop',
          elemValue: 'description'
        },
        {
          elemAttr: 'content',
          elemValue: metaDescription
        }
      ]);
    }
  };

  return {
    generateMetaData: generateMetaData,
    oldMetaData: oldMetaData
  };
};
